import { Content } from "@/ui";
import { getRoute } from "@/getters/getRoute";
import { simple_pages_helper } from "@/helpers/simple_pages_helper";
import { getNestedRoutes } from "@/functions/getNestedRoutes";
import { createTitle } from "@/functions/createTitle";
import sn from "@/snippets";
import useDynamicPage from "@/hooks/useDynamicPage";
import { getDynamicPageStaticProps } from "@/functions/getDynamicPageStaticProps";
import { DynamicPageSeo } from "@/ui/DynamicPageSeo";
import { ContentBlocks } from "@/ui/ContentBlocks";

import * as additionalComponents from "../__components";

export default function Page({ data, prefix, content, title, canonical, seo, isBuilder }) {
  return (
    <>
      {isBuilder ? (
        <>
          <DynamicPageSeo
            {...seo}
            title={seo?.title.length > 0 ? seo.title : title}
            canonical={seo?.canonical.length > 0 ? seo.canonical : canonical}
          />
          <ContentBlocks data={data} prefix={prefix} />
        </>
      ) : (
        <Content items={content} logoCarousel={false} additionalComponents={additionalComponents} />
      )}
    </>
  );
}

export async function getStaticProps({ params: { url_slug } }) {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const builderPage = useDynamicPage(`recruitment-solutions/why-choose-blue-arrow/${url_slug}`);
  if (builderPage) {
    return getDynamicPageStaticProps({ page: builderPage, url_slug });
  }

  const page = simple_pages_helper.find(url_slug, "url_slug");
  const pages = getNestedRoutes({
    url_slugs: ["recruitment-solutions", "why-choose-blue-arrow", url_slug],
    overwrites: { "recruitment-solutions": { href: "/recruitment-solutions/why-choose-blue-arrow/" } },
  });
  const [_page, prevPage] = [...pages].reverse();
  const [p1, p2, p3, p4] = page.narrative_panels;

  let content = [
    { component: "BreadcrumbNavigation", props: { items: pages } },
    sn.header({ page, prevPage }),
    sn.richText({
      page,
      config: { bodyKey: "intro" },
      className: "my-4 my-md-5",
    }),
    ...sn.richText({ page, config: { bodyKey: "" }, after: { component: "Divider" } }),
    ...sn.statCounterWidget({ page, after: { component: "" } }),
    ...sn.trendingJobs({ page, after: { component: "Divider" } }),
    ...sn.narrativePanels({
      page: { narrative_panels: [p1, p2, p3] },
      after: { component: "Divider" },
    }),
    ...sn.trendingJobs({
      page,
      config: {
        titleKey: "trending_jobs_2_title",
        itemsKey: "trending_jobs_2_items",
        iconKey: "Trending_jobs_2_icon",
      },
      after: { component: "Divider" },
    }),
    ...sn.gridButtons({ page, md: 3, after: { component: "Divider" } }),
    ...sn.benefits({ page, after: { component: "Divider" } }),
  ];

  if (p4) {
    content.push(...sn.narrativePanels({ page: { narrative_panels: [p4] } }));
  }

  content = [
    ...content,
    sn.richText({ page, config: { bodyKey: "narrative_panels_footer" } }),
    ...sn.logoCarousel({ page, after: { component: "Divider" } }),
    ...sn.logoCarousel({
      page,
      config: {
        itemsKey: "logo_carousel_2_items",
        titleKey: "logo_carousel_2_title",
      },
      // after: { component: "Divider" },
    }),
    sn.chatbot({ page }),
    sn.promoSection({ page }),
  ];

  return {
    props: {
      meta: {
        title: createTitle(page.title),
        canonical: "https://www.bluearrow.co.uk" + getRoute("productPage", { url_slug: url_slug }),
      },
      content,
    },
  };
}

export async function getStaticPaths() {
  return {
    paths: [
      "care-home-staffing",
      "productivity-solution",
      "public-sector",
      "vms-lite",
      "fulfilment-plus",
      "recruiterbot",
      "client-portal",
      "non-classroom-staffing-education-providers",
      "contact-centre-staffing",
      "university-staffing",
      "hgv-driver-training",
      "catering-and-hospitality-staffing",
      "driving-and-distribution-staffing",
      "industrial-logistics-and-warehouse-staffing",
      "manufacturing",
      "retail",
      "healthcare-and-nhs",
    ].map((url_slug) => ({
      params: { url_slug },
    })),
    fallback: false,
  };
}
